import { useCallback, useEffect, useState } from "react";
import {
  disablePush,
  enablePush,
  getPushEndpoint,
  needsIosInstallForPush,
  pushState,
  type PushState
} from "./push";

// Notification settings: push for THIS device (per-device model, Atelier pattern)
// plus member-level email preferences per category.

const KINDS = ["call", "step", "delivery", "system"] as const;
type Kind = (typeof KINDS)[number];

type Preferences = Record<string, boolean>;

export function NotificationSettings(props: {
  csrfToken: string;
  t: (key: string) => string;
}) {
  const [push, setPush] = useState<PushState>("unsupported");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [devicePrefs, setDevicePrefs] = useState<Record<Kind, boolean> | null>(null);

  const refresh = useCallback(async () => {
    setPush(await pushState());
    try {
      const response = await fetch("/api/notifications/preferences", { credentials: "include" });
      if (response.ok) {
        const data = await response.json() as { preferences: Preferences };
        setPreferences(data.preferences);
      }
    } catch {
      /* karta zůstane bez e-mailových voleb */
    }
    const endpoint = await getPushEndpoint();
    if (endpoint) {
      try {
        const response = await fetch("/api/notifications/device", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ endpoint })
        });
        if (response.ok) {
          const data = await response.json() as { prefs: Record<Kind, boolean> };
          setDevicePrefs(data.prefs);
        }
      } catch {
        setDevicePrefs(null);
      }
    } else {
      setDevicePrefs(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function togglePush() {
    setBusy(true);
    setError(false);
    try {
      setPush(push === "on" ? await disablePush(props.csrfToken) : await enablePush(props.csrfToken));
      await refresh();
    } catch {
      setError(true);
    }
    setBusy(false);
  }

  async function saveDevicePref(kind: Kind, value: boolean) {
    const endpoint = await getPushEndpoint();
    if (!endpoint) return;
    setDevicePrefs((current) => (current ? { ...current, [kind]: value } : current));
    try {
      const response = await fetch("/api/notifications/device/prefs", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
        credentials: "include",
        body: JSON.stringify({ endpoint, patch: { [kind]: value } })
      });
      if (!response.ok) throw new Error("device_prefs_failed");
      const data = await response.json() as { prefs: Record<Kind, boolean> };
      setDevicePrefs(data.prefs);
    } catch {
      setError(true);
      await refresh();
    }
  }

  async function saveEmailPref(column: string, value: boolean) {
    setPreferences((current) => (current ? { ...current, [column]: value } : current));
    try {
      const response = await fetch("/api/notifications/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
        credentials: "include",
        body: JSON.stringify({ [column]: value })
      });
      if (!response.ok) throw new Error("preferences_failed");
      const data = await response.json() as { preferences: Preferences };
      setPreferences(data.preferences);
    } catch {
      setError(true);
      await refresh();
    }
  }

  const pushLabel = props.t(
    push === "on"
      ? "notifications.pushOn"
      : push === "denied"
        ? "notifications.pushDenied"
        : push === "unsupported"
          ? "notifications.pushUnsupported"
          : "notifications.pushOff"
  );

  return (
    <section className="notification-settings" aria-labelledby="notification-settings-title">
      <p className="card-kicker">{props.t("notifications.eyebrow")}</p>
      <h2 id="notification-settings-title">{props.t("notifications.title")}</h2>

      <div className="notification-block">
        <h3>{props.t("notifications.pushDeviceTitle")}</h3>
        <p className="notification-state">{pushLabel}</p>
        {needsIosInstallForPush() ? (
          <p className="notification-hint">{props.t("notifications.pushIosInstall")}</p>
        ) : push === "denied" ? (
          <p className="notification-hint">{props.t("notifications.pushDeniedHint")}</p>
        ) : push !== "unsupported" ? (
          <button className={`${push === "on" ? "secondary-button" : "primary-button"} compact-button notification-push-action`} type="button" disabled={busy} onClick={() => void togglePush()}>
            {props.t(push === "on" ? "notifications.pushDisable" : "notifications.pushEnable")}
          </button>
        ) : null}
        {push === "on" && devicePrefs ? (
          <fieldset className="notification-kinds">
            <legend>{props.t("notifications.pushKindsLegend")}</legend>
            {KINDS.map((kind) => (
              <label className="notification-check" key={kind}>
                <input
                  type="checkbox"
                  checked={devicePrefs[kind]}
                  onChange={(event) => void saveDevicePref(kind, event.target.checked)}
                />
                <span>{props.t(`notifications.kind.${kind}`)}</span>
              </label>
            ))}
          </fieldset>
        ) : null}
      </div>

      {preferences ? (
        <div className="notification-block">
          <h3>{props.t("notifications.emailTitle")}</h3>
          <label className="notification-master notification-check">
            <input
              type="checkbox"
              checked={preferences.email_enabled}
              onChange={(event) => void saveEmailPref("email_enabled", event.target.checked)}
            />
            <span>{props.t("notifications.emailMaster")}</span>
          </label>
          {preferences.email_enabled ? (
            <fieldset className="notification-kinds">
              <legend>{props.t("notifications.emailKindsLegend")}</legend>
              {KINDS.map((kind) => (
                <label className="notification-check" key={kind}>
                  <input
                    type="checkbox"
                    checked={preferences[`email_${kind}`]}
                    onChange={(event) => void saveEmailPref(`email_${kind}`, event.target.checked)}
                  />
                  <span>{props.t(`notifications.kind.${kind}`)}</span>
                </label>
              ))}
            </fieldset>
          ) : null}
          <p className="notification-hint">{props.t("notifications.criticalNote")}</p>
        </div>
      ) : null}

      {error ? <p className="notification-error" role="alert">{props.t("notifications.error")}</p> : null}
    </section>
  );
}
